import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import Logo from './Logo';
import { DecorGold } from './Decoration';

/**
 * Composant Header de page élégant et minimaliste
 * 
 * @param {string} title - Titre de la page
 * @param {string} subtitle - Sous-titre (optionnel)
 * @param {boolean} showLogo - Afficher le logo (défaut: true)
 * @param {boolean} showDecoration - Afficher les décorations (défaut: true)
 * @param {string} variant - 'elegant', 'minimal', 'gradient'
 * @param {object} sx - Styles personnalisés
 */
const PageHeader = ({ 
  title, 
  subtitle, 
  showLogo = true,
  showDecoration = false,
  variant = 'elegant',
  sx = {}
}) => {
  // Variant avec dégradé subtil
  if (variant === 'gradient') {
    return (
      <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.05) 0%, rgba(212, 175, 55, 0.02) 100%)',
          borderBottom: '1px solid rgba(212, 175, 55, 0.2)',
          py: 6,
          mb: 4,
          ...sx
        }}
      >
        <Container maxWidth="lg">
          {showLogo && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Logo size="medium" />
            </Box>
          )}

          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                mb: subtitle ? 2 : 0,
                fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
                letterSpacing: '-0.02em'
              }}
            >
              {title}
            </Typography>

            {subtitle && (
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  fontWeight: 400
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Container>
      </Box>
    );
  }

  // Variant minimal (très épuré)
  if (variant === 'minimal') {
    return (
      <Box sx={{ mb: 4, ...sx }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', py: 4 }}>
            {showLogo && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Logo size="small" />
              </Box>
            )}

            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                mb: subtitle ? 1 : 0,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
              }}
            >
              {title}
            </Typography>

            {subtitle && (
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
              >
                {subtitle}
              </Typography>
            )}

            {showDecoration && (
              <Box
                sx={{
                  width: '80px',
                  height: '2px',
                  bgcolor: '#D4AF37',
                  mx: 'auto',
                  mt: 2
                }}
              />
            )}
          </Box>
        </Container>
      </Box>
    );
  }

  // Variant élégant (par défaut) - avec décorations dorées
  return (
    <Box sx={{ mb: 4, ...sx }}>
      <Container maxWidth="lg">
        {showLogo && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, mt: 4 }}>
            <Logo size="medium" />
          </Box>
        )}

        {showDecoration && (
          <Box sx={{ maxWidth: '300px', mx: 'auto', mb: 2 }}>
            <DecorGold />
          </Box>
        )}

        <Box sx={{ textAlign: 'center', my: 3 }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              mb: subtitle ? 2 : 0,
              fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
              letterSpacing: '-0.01em'
            }}
          >
            {title}
          </Typography>

          {subtitle && (
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                fontWeight: 400,
                maxWidth: '700px',
                mx: 'auto'
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {showDecoration && (
          <Box sx={{ maxWidth: '300px', mx: 'auto', mt: 2 }}>
            <DecorGold />
          </Box>
        )}
      </Container>
    </Box>
  );
};

/**
 * Header simplifié pour les formulaires et modales
 */
export const SimpleHeader = ({ title, icon, sx = {} }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 3,
        pb: 2,
        borderBottom: '2px solid',
        borderColor: '#D4AF37',
        ...sx
      }}
    >
      {icon && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: 'primary.light',
            color: 'primary.contrastText'
          }}
        >
          {icon}
        </Box>
      )}
      <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
    </Box>
  );
};

/**
 * Header avec carte élégante (effet de profondeur)
 */
export const CardHeader = ({ title, subtitle, icon, sx = {} }) => {
  return (
    <Box
      sx={{
        bgcolor: 'white',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        p: 4,
        mb: 4,
        border: '1px solid',
        borderColor: 'rgba(212, 175, 55, 0.2)',
        ...sx
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: 'rgba(212, 175, 55, 0.1)',
              color: '#D4AF37'
            }}
          >
            {icon}
          </Box>
        )}
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              mb: subtitle ? 0.5 : 0
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body1" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Header avec ligne dorée simple
 */
export const LineHeader = ({ title, subtitle, sx = {} }) => {
  return (
    <Box sx={{ mb: 4, ...sx }}>
      <Box
        sx={{
          borderLeft: '4px solid #D4AF37',
          pl: 3,
          py: 2
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            color: 'primary.main',
            mb: subtitle ? 1 : 0
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body1" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PageHeader;
